import React, { useMemo } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  colorForAssignee,
  initialsFor,
  normalizeAssigneeKey,
  preferredAssigneeLabel,
} from '@/lib/assigneeDisplay';
import { useNotifications } from '@/contexts/NotificationsContext';
import { cn } from '@/lib/utils';

interface BarAssigneesStackProps {
  assignees: string[];
  /** Ширина бара в пикселях — для адаптивного поведения. */
  barWidthPx: number;
  /** Макс. видимых аватарок. */
  maxVisible?: number;
  className?: string;
}

/**
 * Стек мини-аватарок, накладываемый на «колбаску» Ганта справа.
 * - Compact-режим при узком баре: только иконка «люди» с числом.
 * - Hover/tap → popover со списком.
 */
export function BarAssigneesStack({
  assignees,
  barWidthPx,
  maxVisible = 3,
  className,
}: BarAssigneesStackProps) {
  const { assigneeMatches } = useNotifications();

  const normalized = useMemo(
    () =>
      assignees
        .map((raw) => {
          const key = normalizeAssigneeKey(raw);
          return {
            raw,
            key,
            match: assigneeMatches.get(key) ?? null,
          };
        })
        .filter((x) => x.raw.trim().length > 0),
    [assignees, assigneeMatches],
  );

  if (normalized.length === 0) return null;

  const compact = barWidthPx < 56;
  const visibleCap = compact ? 1 : maxVisible;
  const visible = normalized.slice(0, visibleCap);
  const overflow = normalized.length - visible.length;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className={cn(
            'pointer-events-auto absolute right-0.5 top-1/2 z-[8] -translate-y-1/2 flex items-center gap-[-4px] rounded-full bg-white/0 px-0.5 py-0.5 outline-none transition-transform hover:scale-105 focus-visible:ring-2 focus-visible:ring-white/70',
            className,
          )}
          aria-label={`Исполнители (${normalized.length})`}
          title={normalized
            .map((n) => preferredAssigneeLabel(n.raw, n.match))
            .join(', ')}
        >
          <span className="flex items-center -space-x-1.5">
            {visible.map((n) => {
              const bg = n.match ? colorForAssignee(normalizeAssigneeKey(n.match.login)) : '#94A3B8';
              const label = preferredAssigneeLabel(n.raw, n.match);
              return (
                <span
                  key={n.key}
                  aria-hidden
                  className={cn(
                    'inline-flex size-4 shrink-0 items-center justify-center rounded-full text-[8px] font-semibold leading-none text-white ring-1 ring-white/80 shadow-sm',
                    !n.match && 'border border-dashed border-white/70',
                  )}
                  style={{ backgroundColor: bg }}
                >
                  {initialsFor(label)}
                </span>
              );
            })}
            {overflow > 0 ? (
              <span
                aria-hidden
                className="inline-flex h-4 min-w-[18px] shrink-0 items-center justify-center rounded-full bg-black/55 px-1 text-[9px] font-semibold leading-none text-white ring-1 ring-white/80 shadow-sm"
              >
                +{overflow}
              </span>
            ) : null}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="top"
        sideOffset={6}
        className="z-[60] w-[min(22rem,calc(100vw-1.5rem))] p-2 shadow-lg"
      >
        <div className="mb-1 px-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Исполнители задачи
        </div>
        <ul className="flex flex-col gap-1">
          {normalized.map((n) => {
            const label = preferredAssigneeLabel(n.raw, n.match);
            const bg = n.match
              ? colorForAssignee(normalizeAssigneeKey(n.match.login))
              : '#94A3B8';
            return (
              <li
                key={n.key}
                className="flex items-center gap-2 rounded px-1.5 py-1 hover:bg-muted/50"
              >
                <span
                  aria-hidden
                  className="inline-flex size-7 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold text-white shadow-sm"
                  style={{ backgroundColor: bg }}
                >
                  {initialsFor(label)}
                </span>
                <div className="min-w-0 flex-1 leading-tight">
                  <div className="truncate text-sm">{label}</div>
                  <div className="truncate text-[11px] text-muted-foreground">
                    {n.match ? (
                      <>
                        {n.match.login}
                        {' · '}
                        {n.match.isRegistered ? (
                          <span className="text-emerald-600">зарегистрирован</span>
                        ) : (
                          <span className="text-amber-600">приглашён</span>
                        )}
                      </>
                    ) : (
                      <span className="text-muted-foreground">не найден — уведомление не будет отправлено</span>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
